import { emojiMatch } from 'src/constants'
import { usModelProductNumbers } from 'src/features/atoms/glove/us/constants/baseModel'
import ko, { leagueKoOptions } from 'src/locales/ko'
import { Brand, Agency, Embroidery, I18NText, Locale, Model, Position, ShoesModel, StateObj, WebPartsObj, Personal } from 'src/types'

export const getStartSimulationMessage = (selectedModel: Model | ShoesModel, isEn: boolean, t: I18NText): string => {
  const message = isEn
    ? `${t.CONFIRM_START_SIMULATION}${selectedModel?.productNumber}`
    : `${selectedModel?.productNumber}${t.CONFIRM_START_SIMULATION}`
  return message
}

export const webPartsPositionFilter = (webPartsObjects: WebPartsObj[], position: Position): WebPartsObj[] => {
  return webPartsObjects.filter((webPart) => webPart.position.includes(position))
}

export const first = (array: StateObj[]): StateObj => array[0]
export const objectsFilter = (selected: string, objects: StateObj[]): StateObj => first(objects.filter((o) => o.value === selected))

export const embroideryFlagGenerator = (e: Embroidery) => {
  const { color, shadowColor, edgeColor, pointColor } = e
  const isShadowColor = shadowColor.value !== 'none' && shadowColor.value !== 'unselected'
  const isEdgeColor = edgeColor.value !== 'none' && edgeColor.value !== 'unselected'
  const isArribaFont = e.typeFace.value === 'arriba'
  const isUnSelectedPosition = e.position.value === ''
  const isUnSelectedTypeFace = e.typeFace.value === ''
  const isUnSelectedMainColor = e.color.value === 'unselected'
  const isUnselectedEmbroideryColor = color.value.length === 0
  const isSelectedTypeFace = [e.typeFace.label, e.typeFace.value].every((x) => x.length > 0)
  const disabledShadowColor = isEdgeColor || isUnselectedEmbroideryColor || !isSelectedTypeFace
  const disabledEdgeColor = isShadowColor || isUnselectedEmbroideryColor || !isSelectedTypeFace
  const shadowColorLabel = shadowColor.label
  const edgeColorLabel = edgeColor.label
  const pointColorLabel = pointColor?.label

  return {
    shadowColorLabel,
    edgeColorLabel,
    pointColorLabel,
    disabledShadowColor,
    disabledEdgeColor,
    isArribaFont,
    isUnSelectedPosition,
    isUnSelectedTypeFace,
    isUnSelectedMainColor,
    isSelectedTypeFace
  }
}

export const isHalfWidthCharChecker = (str: string) => !str.match(/^[^\x01-\x7E\xA1-\xDF]+$/)

// n-1番目までのembroideryで指定された刺繍位置のvalueリストを返す
export const embroideryPositionListGenerator = (embroideries: Embroidery[], overAllIndex: number) =>
  embroideries
    .map((e, i) => {
      if (i < overAllIndex) {
        return e.position.value
      }
    })
    .filter(Boolean)

export const embroideryPositionFilter = (
  positionObjects: { label: string; value: string }[],
  selectedPosition: string[]
): {
  label: string
  value: string
}[] => positionObjects.filter((p) => !selectedPosition.includes(p.value))

// TODO: selectablePositionGenerator内に YSRの関心が入るのは好ましくないため、YSRのロゴ2が選択されているかどうかを引数として受け取る形にリファクタする
export const selectablePositionGenerator = (
  embroideries: Embroidery[],
  positionObjects: StateObj[],
  excludes: string[],
  isBuriedLiningFirstByYsrDirectLogo2Position: boolean, // 裏平にYSRダイレクトロゴ2が選択されていたら、裏平への通常刺繍は不可にする
  index: number
): StateObj[] => {
  const selectedPosition = embroideryPositionListGenerator(embroideries, index)
  const selectablePositionBase = embroideryPositionFilter(positionObjects, selectedPosition)
  const overAllSelectedPositions = embroideries.map((e) => e.position.value)
  // 裏平1段目が選択可能かどうか→可能な時は、2段目は選択できないようにしておきたい
  const isSelectableOfLiningFirst = selectablePositionBase.some((p) => p.value === 'leatherLiningFirst')
  const selectablePosition = (
    isSelectableOfLiningFirst ? selectablePositionBase.filter((p) => p.value !== 'leatherLiningSecond') : selectablePositionBase
  )
    .filter((p) => (!overAllSelectedPositions.includes(p.value) || p.value === embroideries[index].position?.value) && !excludes.includes(p.value))
    .filter((position) =>
      isBuriedLiningFirstByYsrDirectLogo2Position ? !['leatherLiningFirst', 'leatherLiningSecond'].includes(position.value) : true
    )
  return selectablePosition
}

export const generateSubColors = (shouldFiltering: boolean, colors: StateObj[]) => {
  return shouldFiltering ? colors.filter((o) => o.value === 'none') : colors
}

export const positionChecker = (position: Position) => {
  const isPitcher = position === 'pitcher'
  const isCatcher = position === 'catcher'
  const isFirst = position === 'first'
  // const isInfielder = position === 'infielder'
  const isOutfielder = position === 'outfielder'
  const isGlove = ['pitcher', 'infielder', 'outfielder'].includes(position)
  const isMitt = ['first', 'catcher'].includes(position)

  return {
    isPitcher,
    isCatcher,
    isFirst,
    // isInfielder, // 未使用
    isOutfielder,
    isGlove,
    isMitt
  }
}

export const backStyleChecker = (backStyle: string) => {
  const isFirstBackStyle = backStyle === 'firstBackStyle'
  return {
    isFirstBackStyle
  }
}

// TODO: これいらなくなる？
export const contentsChecker = (stateObj: StateObj) => {
  const isUsContents = stateObj.label && stateObj.label.charCodeAt(0) < 256
  const isKoreaContents = stateObj.label && stateObj.label.charCodeAt(0) >= 40001
  const isTaiwanContents = stateObj.label && stateObj.label.charCodeAt(0) >= 19968 && stateObj.label.charCodeAt(0) <= 40959
  return { isUsContents, isKoreaContents, isTaiwanContents }
}

export const sendEmailConfirmMessage = (agency: Agency, locale: Locale): string => {
  const { isJa, isEn } = locale
  if (isEn) {
    return `Do you want to place an order with ${agency.name} ?`
  }
  if (isJa) {
    return `${agency.name}に発注しますか？`
  }
  return `${agency.name}에게 발주하시겠습니까?`
}

export const brandNameIdentifier = (brand: Brand) => {
  switch (brand) {
    case 'atoms':
      return 'ATOMS'
    case 'yellStory':
      return 'Yell-Story'
    case 'dxm':
      return 'DxM'
    case 'gorilla':
      return 'ごりら印の野球用具'
  }
}

export const getAgencyStatus = (agency: Agency | null, retail: { name: string; email: string }) => {
  if (retail === null) {
    return { isChinaAgency2AtomsMain: false }
  }
  // 中国代理店からATOMS本社への発注は不可
  const isChinaAgency2AtomsMain = agency.name === 'ATOMS本社' && ['吉本常株式会社/中国代理店'].includes(retail.name)
  return { isChinaAgency2AtomsMain }
}

export const payloadLeague = (isKoreaContents: boolean, personal: Personal): string => {
  if (!isKoreaContents) return personal.league
  return leagueKoOptions.find((o) => o.label === personal.league)?.value || ''
}

export const emojiChecker = (text: string) => {
  const regex = new RegExp(emojiMatch)
  return regex.test(text)
}

export const generateSubject = (savedId: string, brand: Brand, isJa: boolean, isEn: boolean, isTw: boolean = false, isKo: boolean = false) => {
  const brandName = brandNameIdentifier(brand)
  if (isJa) {
    return `【${brandName} オーダーシミュレーション】保存IDが発行されました ${savedId}`
  }
  if (isEn) {
    return `[${brandName} Order Simulation] Preservation ID has been issued. ${savedId}`
  }
  if (isTw) {
    return `【${brandName} 訂購模擬】已發出保存 ID ${savedId}`
  }
  if (isKo) {
    return `[${brandName} 주문 시뮬레이션] 저장 아이디가 발급되었습니다. ${savedId}`
  }
  // デフォルトは日本語
  return `【${brandName} オーダーシミュレーション】保存IDが発行されました ${savedId}`
}

export const isInvalidPersonalData = (personal: Personal): boolean => {
  if (!personal?.league || personal?.league?.length === 0) return true
  if (!personal?.position || personal?.position?.length === 0) return true
  // if (!personal?.mailAddress || personal?.mailAddress?.length === 0) return true
  return false
}

export const usModelOfAtomsChecker = (productNumberOfSelectedModel: string): boolean => usModelProductNumbers.includes(productNumberOfSelectedModel)
